// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { AzureSupportedClouds } from "./static-helpers/cloudSettingHelpers.js";
import { AzureClouds } from "./static-helpers/cloudSettingHelpers.js";
import type {
  PageSettings,
  ContinuablePage,
  PagedAsyncIterableIterator,
} from "./static-helpers/pagingHelpers.js";

export { CosmosDBManagementClient } from "./cosmosDBManagementClient.js";
export type { SimplePollerLike } from "./static-helpers/simplePollerHelpers.js";
export type { RestorePollerOptions } from "./restorePollerHelpers.js";
export { restorePoller } from "./restorePollerHelpers.js";
export type {
  Operation,
  OperationDisplay,
  ErrorResponse,
  ErrorDetail,
  ErrorAdditionalInfo,
  DatabaseAccountGetResults,
  DatabaseAccountGetProperties,
  IpAddressOrRange,
  ConsistencyPolicy,
  DefaultConsistencyLevel,
  Capability,
  Location,
  FailoverPolicy,
  VirtualNetworkRule,
  PrivateEndpointConnection,
  PrivateEndpointConnectionProperties,
  PrivateEndpointProperty,
  PrivateLinkServiceConnectionStateProperty,
  ConnectorOffer,
  PublicNetworkAccess,
  ApiProperties,
  ServerVersion,
  AnalyticalStorageConfiguration,
  AnalyticalStorageSchemaType,
  CreateMode,
  RestoreParameters,
  RestoreMode,
  DatabaseRestoreResource,
  GremlinDatabaseRestoreResource,
  BackupPolicy,
  BackupPolicyUnion,
  BackupPolicyType,
  BackupPolicyMigrationState,
  BackupPolicyMigrationStatus,
  PeriodicModeBackupPolicy,
  PeriodicModeProperties,
  BackupStorageRedundancy,
  ContinuousModeBackupPolicy,
  ContinuousModeProperties,
  ContinuousTier,
  CorsPolicy,
  NetworkAclBypass,
  Capacity,
  DatabaseAccountKeysMetadata,
  AccountKeyMetadata,
  MinimalTlsVersion,
  DefaultPriorityLevel,
  ManagedServiceIdentity,
  ResourceIdentityType,
  Components1Jq1T4ISchemasManagedserviceidentityPropertiesUserassignedidentitiesAdditionalproperties,
  DatabaseAccountKind,
  ProxyResource,
  Resource,
  SystemData,
  CreatedByType,
  RestoreParametersBase,
  DatabaseAccountCreateUpdateParameters,
  DatabaseAccountCreateUpdateProperties,
  ARMResourceProperties,
  DatabaseAccountUpdateParameters,
  DatabaseAccountUpdateProperties,
  FailoverPolicies,
  DatabaseAccountListKeysResult,
  DatabaseAccountListReadOnlyKeysResult,
  DatabaseAccountListConnectionStringsResult,
  DatabaseAccountConnectionString,
  Kind,
  Type,
  RegionForOnlineOffline,
  DatabaseAccountRegenerateKeyParameters,
  KeyKind,
  Metric,
  UnitType,
  MetricName,
  MetricValue,
  Usage,
  MetricDefinition,
  MetricAvailability,
  PrimaryAggregationType,
  SqlDatabaseGetResults,
  SqlDatabaseGetProperties,
  SqlDatabaseGetPropertiesResource,
  SqlDatabaseGetPropertiesOptions,
  SqlDatabaseResource,
  ResourceRestoreParameters,
  OptionsResource,
  AutoscaleSettings,
  SqlDatabaseCreateUpdateParameters,
  SqlDatabaseCreateUpdateProperties,
  CreateUpdateOptions,
  ThroughputSettingsGetResults,
  ThroughputSettingsGetProperties,
  ThroughputSettingsGetPropertiesResource,
  ThroughputSettingsResource,
  AutoscaleSettingsResource,
  AutoUpgradePolicyResource,
  ThroughputPolicyResource,
  ThroughputSettingsUpdateParameters,
  ThroughputSettingsUpdateProperties,
  CloudError,
  CosmosDBErrorResult,
  ClientEncryptionKeyGetResults,
  ClientEncryptionKeyGetProperties,
  ClientEncryptionKeyGetPropertiesResource,
  ClientEncryptionKeyResource,
  KeyWrapMetadata,
  ClientEncryptionKeyCreateUpdateParameters,
  ClientEncryptionKeyCreateUpdateProperties,
  SqlContainerGetResults,
  SqlContainerGetProperties,
  SqlContainerGetPropertiesResource,
  SqlContainerGetPropertiesOptions,
  SqlContainerResource,
  IndexingPolicy,
  IndexingMode,
  IncludedPath,
  Indexes,
  DataType,
  IndexKind,
  ExcludedPath,
  CompositePath,
  CompositePathSortOrder,
  SpatialSpec,
  SpatialType,
  VectorIndex,
  VectorIndexType,
  FullTextIndexPath,
  ContainerPartitionKey,
  PartitionKind,
  UniqueKeyPolicy,
  UniqueKey,
  ConflictResolutionPolicy,
  ConflictResolutionMode,
  ClientEncryptionPolicy,
  ClientEncryptionIncludedPath,
  ComputedProperty,
  VectorEmbeddingPolicy,
  VectorEmbedding,
  VectorDataType,
  DistanceFunction,
  FullTextPolicy,
  FullTextPath,
  SqlContainerCreateUpdateParameters,
  SqlContainerCreateUpdateProperties,
  ContinuousBackupRestoreLocation,
  BackupInformation,
  ContinuousBackupInformation,
  SqlStoredProcedureGetResults,
  SqlStoredProcedureGetProperties,
  SqlStoredProcedureGetPropertiesResource,
  SqlStoredProcedureResource,
  SqlStoredProcedureCreateUpdateParameters,
  SqlStoredProcedureCreateUpdateProperties,
  SqlUserDefinedFunctionGetResults,
  SqlUserDefinedFunctionGetProperties,
  SqlUserDefinedFunctionGetPropertiesResource,
  SqlUserDefinedFunctionResource,
  SqlUserDefinedFunctionCreateUpdateParameters,
  SqlUserDefinedFunctionCreateUpdateProperties,
  SqlTriggerGetResults,
  SqlTriggerGetProperties,
  SqlTriggerGetPropertiesResource,
  SqlTriggerResource,
  TriggerType,
  TriggerOperation,
  SqlTriggerCreateUpdateParameters,
  SqlTriggerCreateUpdateProperties,
  SqlRoleDefinitionGetResults,
  SqlRoleDefinitionResource,
  RoleDefinitionType,
  Permission,
  SqlRoleDefinitionCreateUpdateParameters,
  SqlRoleAssignmentGetResults,
  SqlRoleAssignmentResource,
  SqlRoleAssignmentCreateUpdateParameters,
  MongoDBDatabaseGetResults,
  MongoDBDatabaseGetProperties,
  MongoDBDatabaseGetPropertiesResource,
  MongoDBDatabaseGetPropertiesOptions,
  MongoDBDatabaseResource,
  MongoDBDatabaseCreateUpdateParameters,
  MongoDBDatabaseCreateUpdateProperties,
  MongoDBCollectionGetResults,
  MongoDBCollectionGetProperties,
  MongoDBCollectionGetPropertiesResource,
  MongoDBCollectionGetPropertiesOptions,
  MongoDBCollectionResource,
  MongoIndex,
  MongoIndexKeys,
  MongoIndexOptions,
  MongoDBCollectionCreateUpdateParameters,
  MongoDBCollectionCreateUpdateProperties,
  MongoRoleDefinitionGetResults,
  MongoRoleDefinitionResource,
  MongoRoleDefinitionType,
  Privilege,
  PrivilegeResource,
  Role,
  MongoRoleDefinitionCreateUpdateParameters,
  MongoUserDefinitionGetResults,
  MongoUserDefinitionResource,
  MongoUserDefinitionCreateUpdateParameters,
  TableGetResults,
  TableGetProperties,
  TableGetPropertiesResource,
  TableGetPropertiesOptions,
  TableResource,
  TableCreateUpdateParameters,
  TableCreateUpdateProperties,
  TableRoleDefinitionResource,
  TableRoleDefinitionResourceProperties,
  TableRoleAssignmentResource,
  TableRoleAssignmentResourceProperties,
  CassandraKeyspaceGetResults,
  CassandraKeyspaceGetProperties,
  CassandraKeyspaceGetPropertiesResource,
  CassandraKeyspaceGetPropertiesOptions,
  CassandraKeyspaceResource,
  CassandraKeyspaceCreateUpdateParameters,
  CassandraKeyspaceCreateUpdateProperties,
  CassandraTableGetResults,
  CassandraTableGetProperties,
  CassandraTableGetPropertiesResource,
  CassandraTableGetPropertiesOptions,
  CassandraTableResource,
  CassandraSchema,
  Column,
  CassandraPartitionKey,
  ClusterKey,
  CassandraTableCreateUpdateParameters,
  CassandraTableCreateUpdateProperties,
  CassandraRoleDefinitionResource,
  CassandraRoleDefinitionResourceProperties,
  CassandraRoleAssignmentResource,
  CassandraRoleAssignmentResourceProperties,
  GremlinDatabaseGetResults,
  GremlinDatabaseGetProperties,
  GremlinDatabaseGetPropertiesResource,
  GremlinDatabaseGetPropertiesOptions,
  GremlinDatabaseResource,
  GremlinDatabaseCreateUpdateParameters,
  GremlinDatabaseCreateUpdateProperties,
  GremlinGraphGetResults,
  GremlinGraphGetProperties,
  GremlinGraphGetPropertiesResource,
  GremlinGraphGetPropertiesOptions,
  GremlinGraphResource,
  GremlinGraphCreateUpdateParameters,
  GremlinGraphCreateUpdateProperties,
  GremlinRoleDefinitionResource,
  GremlinRoleDefinitionResourceProperties,
  GremlinRoleAssignmentResource,
  GremlinRoleAssignmentResourceProperties,
  NotebookWorkspace,
  NotebookWorkspaceProperties,
  NotebookWorkspaceName,
  NotebookWorkspaceCreateUpdateParameters,
  ARMProxyResource,
  NotebookWorkspaceConnectionInfoResult,
  PrivateLinkResource,
  PrivateLinkResourceProperties,
  PercentileMetric,
  PercentileMetricValue,
  PartitionMetric,
  PartitionUsage,
  LocationGetResult,
  LocationProperties,
  Status,
  ClusterResource,
  ClusterResourceProperties,
  ManagedCassandraProvisioningState,
  AuthenticationMethod,
  SeedNode,
  AutoReplicate,
  Certificate,
  CassandraError,
  BackupSchedule,
  ScheduledEventStrategy,
  AzureConnectionType,
  ManagedCassandraManagedServiceIdentity,
  ManagedCassandraResourceIdentityType,
  CommandPostBody,
  CommandOutput,
  CassandraClusterPublicStatus,
  ManagedCassandraReaperStatus,
  ConnectionError,
  ConnectionState,
  CassandraClusterPublicStatusDataCentersItem,
  ComponentsM9L909SchemasCassandraclusterpublicstatusPropertiesDatacentersItemsPropertiesNodesItems,
  NodeState,
  DataCenterResource,
  DataCenterResourceProperties,
  AuthenticationMethodLdapProperties,
  RestorableDatabaseAccountGetResult,
  RestorableDatabaseAccountProperties,
  ApiType,
  RestorableLocationResource,
  RestorableSqlDatabaseGetResult,
  RestorableSqlDatabaseProperties,
  RestorableSqlDatabasePropertiesResource,
  OperationType,
  RestorableSqlDatabasePropertiesResourceDatabase,
  RestorableSqlContainerGetResult,
  RestorableSqlContainerProperties,
  RestorableSqlContainerPropertiesResource,
  RestorableSqlContainerPropertiesResourceContainer,
  RestorableSqlResourcesGetResult,
  RestorableMongodbDatabaseGetResult,
  RestorableMongodbDatabaseProperties,
  RestorableMongodbDatabasePropertiesResource,
  RestorableMongodbCollectionGetResult,
  RestorableMongodbCollectionProperties,
  RestorableMongodbCollectionPropertiesResource,
  RestorableMongodbResourcesGetResult,
  RestorableGremlinDatabaseGetResult,
  RestorableGremlinDatabaseProperties,
  RestorableGremlinDatabasePropertiesResource,
  RestorableGremlinGraphGetResult,
  RestorableGremlinGraphProperties,
  RestorableGremlinGraphPropertiesResource,
  RestorableGremlinResourcesGetResult,
  RestorableTableGetResult,
  RestorableTableProperties,
  RestorableTablePropertiesResource,
  RestorableTableResourcesGetResult,
  ServiceResource,
  ServiceResourceProperties,
  ServiceResourcePropertiesUnion,
  ServiceSize,
  ServiceType,
  ServiceStatus,
  DataTransferServiceResourceProperties,
  DataTransferRegionalServiceResource,
  SqlDedicatedGatewayServiceResourceProperties,
  DedicatedGatewayType,
  SqlDedicatedGatewayRegionalServiceResource,
  GraphAPIComputeServiceResourceProperties,
  GraphAPIComputeRegionalServiceResource,
  MaterializedViewsBuilderServiceResourceProperties,
  MaterializedViewsBuilderRegionalServiceResource,
  RegionalServiceResource,
  ServiceResourceCreateUpdateParameters,
  ServiceResourceCreateUpdateProperties,
  ServiceResourceCreateUpdatePropertiesUnion,
  DataTransferServiceResourceCreateUpdateProperties,
  SqlDedicatedGatewayServiceResourceCreateUpdateProperties,
  GraphAPIComputeServiceResourceCreateUpdateProperties,
  MaterializedViewsBuilderServiceResourceCreateUpdateProperties,
  MongoMIRoleDefinitionResource,
  MongoMIRoleDefinitionResourceProperties,
  MongoMIRoleAssignmentResource,
  MongoMIRoleAssignmentResourceProperties,
  FleetResource,
  FleetResourceProperties,
  TrackedResource,
  FleetResourceUpdate,
  FleetspaceResource,
  FleetspaceProperties,
  FleetspacePropertiesFleetspaceApiKind,
  FleetspacePropertiesServiceTier,
  FleetspacePropertiesThroughputPoolConfiguration,
  FleetspaceUpdate,
  FleetspaceAccountResource,
  FleetspaceAccountProperties,
  FleetspaceAccountPropertiesGlobalDatabaseAccountProperties,
  DatabaseAccountsCheckNameExistsResponse,
} from "./models/index.js";
export {
  KnownConnectorOffer,
  KnownPublicNetworkAccess,
  KnownServerVersion,
  KnownAnalyticalStorageSchemaType,
  KnownCreateMode,
  KnownRestoreMode,
  KnownBackupPolicyType,
  KnownBackupPolicyMigrationStatus,
  KnownBackupStorageRedundancy,
  KnownContinuousTier,
  KnownMinimalTlsVersion,
  KnownDefaultPriorityLevel,
  KnownDatabaseAccountKind,
  KnownCreatedByType,
  KnownKind,
  KnownType,
  KnownKeyKind,
  KnownUnitType,
  KnownPrimaryAggregationType,
  KnownIndexingMode,
  KnownDataType,
  KnownIndexKind,
  KnownCompositePathSortOrder,
  KnownSpatialType,
  KnownVectorIndexType,
  KnownPartitionKind,
  KnownConflictResolutionMode,
  KnownVectorDataType,
  KnownDistanceFunction,
  KnownTriggerType,
  KnownTriggerOperation,
  KnownNotebookWorkspaceName,
  KnownStatus,
  KnownManagedCassandraProvisioningState,
  KnownAuthenticationMethod,
  KnownAutoReplicate,
  KnownScheduledEventStrategy,
  KnownAzureConnectionType,
  KnownManagedCassandraResourceIdentityType,
  KnownConnectionState,
  KnownNodeState,
  KnownApiType,
  KnownOperationType,
  KnownServiceSize,
  KnownServiceType,
  KnownServiceStatus,
  KnownDedicatedGatewayType,
  KnownFleetspacePropertiesFleetspaceApiKind,
  KnownFleetspacePropertiesServiceTier,
  KnownVersions,
} from "./models/index.js";
export type { CosmosDBManagementClientOptionalParams } from "./api/index.js";
export type {
  CassandraClustersStatusOptionalParams,
  CassandraClustersStartOptionalParams,
  CassandraClustersDeallocateOptionalParams,
  CassandraClustersInvokeCommandOptionalParams,
  CassandraClustersListBySubscriptionOptionalParams,
  CassandraClustersListByResourceGroupOptionalParams,
  CassandraClustersDeleteOptionalParams,
  CassandraClustersUpdateOptionalParams,
  CassandraClustersCreateUpdateOptionalParams,
  CassandraClustersGetOptionalParams,
} from "./api/cassandraClusters/index.js";
export type {
  CassandraDataCentersListOptionalParams,
  CassandraDataCentersDeleteOptionalParams,
  CassandraDataCentersUpdateOptionalParams,
  CassandraDataCentersCreateUpdateOptionalParams,
  CassandraDataCentersGetOptionalParams,
} from "./api/cassandraDataCenters/index.js";
export type {
  CassandraResourcesListCassandraRoleAssignmentsOptionalParams,
  CassandraResourcesDeleteCassandraRoleAssignmentOptionalParams,
  CassandraResourcesCreateUpdateCassandraRoleAssignmentOptionalParams,
  CassandraResourcesGetCassandraRoleAssignmentOptionalParams,
  CassandraResourcesListCassandraRoleDefinitionsOptionalParams,
  CassandraResourcesDeleteCassandraRoleDefinitionOptionalParams,
  CassandraResourcesCreateUpdateCassandraRoleDefinitionOptionalParams,
  CassandraResourcesGetCassandraRoleDefinitionOptionalParams,
  CassandraResourcesListCassandraTablesOptionalParams,
  CassandraResourcesDeleteCassandraTableOptionalParams,
  CassandraResourcesCreateUpdateCassandraTableOptionalParams,
  CassandraResourcesGetCassandraTableOptionalParams,
  CassandraResourcesListCassandraKeyspacesOptionalParams,
  CassandraResourcesDeleteCassandraKeyspaceOptionalParams,
  CassandraResourcesCreateUpdateCassandraKeyspaceOptionalParams,
  CassandraResourcesGetCassandraKeyspaceOptionalParams,
  CassandraResourcesMigrateCassandraTableToManualThroughputOptionalParams,
  CassandraResourcesMigrateCassandraTableToAutoscaleOptionalParams,
  CassandraResourcesUpdateCassandraTableThroughputOptionalParams,
  CassandraResourcesGetCassandraTableThroughputOptionalParams,
  CassandraResourcesMigrateCassandraKeyspaceToManualThroughputOptionalParams,
  CassandraResourcesMigrateCassandraKeyspaceToAutoscaleOptionalParams,
  CassandraResourcesUpdateCassandraKeyspaceThroughputOptionalParams,
  CassandraResourcesGetCassandraKeyspaceThroughputOptionalParams,
} from "./api/cassandraResources/index.js";
export type {
  CollectionListMetricDefinitionsOptionalParams,
  CollectionListUsagesOptionalParams,
  CollectionListMetricsOptionalParams,
} from "./api/collection/index.js";
export type {
  CollectionPartitionListUsagesOptionalParams,
  CollectionPartitionListMetricsOptionalParams,
} from "./api/collectionPartition/index.js";
export type { CollectionPartitionRegionListMetricsOptionalParams } from "./api/collectionPartitionRegion/index.js";
export type { CollectionRegionListMetricsOptionalParams } from "./api/collectionRegion/index.js";
export type {
  DatabaseListMetricDefinitionsOptionalParams,
  DatabaseListUsagesOptionalParams,
  DatabaseListMetricsOptionalParams,
} from "./api/database/index.js";
export type { DatabaseAccountRegionListMetricsOptionalParams } from "./api/databaseAccountRegion/index.js";
export type {
  DatabaseAccountsCheckNameExistsOptionalParams,
  DatabaseAccountsListMetricDefinitionsOptionalParams,
  DatabaseAccountsListUsagesOptionalParams,
  DatabaseAccountsListMetricsOptionalParams,
  DatabaseAccountsRegenerateKeyOptionalParams,
  DatabaseAccountsListReadOnlyKeysOptionalParams,
  DatabaseAccountsGetReadOnlyKeysOptionalParams,
  DatabaseAccountsOnlineRegionOptionalParams,
  DatabaseAccountsOfflineRegionOptionalParams,
  DatabaseAccountsListConnectionStringsOptionalParams,
  DatabaseAccountsListKeysOptionalParams,
  DatabaseAccountsFailoverPriorityChangeOptionalParams,
  DatabaseAccountsListOptionalParams,
  DatabaseAccountsListByResourceGroupOptionalParams,
  DatabaseAccountsDeleteOptionalParams,
  DatabaseAccountsUpdateOptionalParams,
  DatabaseAccountsCreateOrUpdateOptionalParams,
  DatabaseAccountsGetOptionalParams,
} from "./api/databaseAccounts/index.js";
export type {
  FleetListOptionalParams,
  FleetListByResourceGroupOptionalParams,
  FleetDeleteOptionalParams,
  FleetUpdateOptionalParams,
  FleetCreateOptionalParams,
  FleetGetOptionalParams,
} from "./api/fleet/index.js";
export type {
  FleetspaceListOptionalParams,
  FleetspaceDeleteOptionalParams,
  FleetspaceUpdateOptionalParams,
  FleetspaceCreateOptionalParams,
  FleetspaceGetOptionalParams,
} from "./api/fleetspace/index.js";
export type {
  FleetspaceAccountListOptionalParams,
  FleetspaceAccountDeleteOptionalParams,
  FleetspaceAccountCreateOptionalParams,
  FleetspaceAccountGetOptionalParams,
} from "./api/fleetspaceAccount/index.js";
export type {
  GremlinResourcesListGremlinRoleAssignmentsOptionalParams,
  GremlinResourcesDeleteGremlinRoleAssignmentOptionalParams,
  GremlinResourcesCreateUpdateGremlinRoleAssignmentOptionalParams,
  GremlinResourcesGetGremlinRoleAssignmentOptionalParams,
  GremlinResourcesListGremlinRoleDefinitionsOptionalParams,
  GremlinResourcesDeleteGremlinRoleDefinitionOptionalParams,
  GremlinResourcesCreateUpdateGremlinRoleDefinitionOptionalParams,
  GremlinResourcesGetGremlinRoleDefinitionOptionalParams,
  GremlinResourcesRetrieveContinuousBackupInformationOptionalParams,
  GremlinResourcesListGremlinGraphsOptionalParams,
  GremlinResourcesDeleteGremlinGraphOptionalParams,
  GremlinResourcesCreateUpdateGremlinGraphOptionalParams,
  GremlinResourcesGetGremlinGraphOptionalParams,
  GremlinResourcesListGremlinDatabasesOptionalParams,
  GremlinResourcesDeleteGremlinDatabaseOptionalParams,
  GremlinResourcesCreateUpdateGremlinDatabaseOptionalParams,
  GremlinResourcesGetGremlinDatabaseOptionalParams,
  GremlinResourcesMigrateGremlinGraphToManualThroughputOptionalParams,
  GremlinResourcesMigrateGremlinGraphToAutoscaleOptionalParams,
  GremlinResourcesUpdateGremlinGraphThroughputOptionalParams,
  GremlinResourcesGetGremlinGraphThroughputOptionalParams,
  GremlinResourcesMigrateGremlinDatabaseToManualThroughputOptionalParams,
  GremlinResourcesMigrateGremlinDatabaseToAutoscaleOptionalParams,
  GremlinResourcesUpdateGremlinDatabaseThroughputOptionalParams,
  GremlinResourcesGetGremlinDatabaseThroughputOptionalParams,
} from "./api/gremlinResources/index.js";
export type {
  LocationsListOptionalParams,
  LocationsGetOptionalParams,
} from "./api/locations/index.js";
export type {
  MongoDBResourcesListMongoUserDefinitionsOptionalParams,
  MongoDBResourcesDeleteMongoUserDefinitionOptionalParams,
  MongoDBResourcesCreateUpdateMongoUserDefinitionOptionalParams,
  MongoDBResourcesGetMongoUserDefinitionOptionalParams,
  MongoDBResourcesListMongoRoleDefinitionsOptionalParams,
  MongoDBResourcesDeleteMongoRoleDefinitionOptionalParams,
  MongoDBResourcesCreateUpdateMongoRoleDefinitionOptionalParams,
  MongoDBResourcesGetMongoRoleDefinitionOptionalParams,
  MongoDBResourcesRetrieveContinuousBackupInformationOptionalParams,
  MongoDBResourcesListMongoDBCollectionsOptionalParams,
  MongoDBResourcesDeleteMongoDBCollectionOptionalParams,
  MongoDBResourcesCreateUpdateMongoDBCollectionOptionalParams,
  MongoDBResourcesGetMongoDBCollectionOptionalParams,
  MongoDBResourcesListMongoDBDatabasesOptionalParams,
  MongoDBResourcesDeleteMongoDBDatabaseOptionalParams,
  MongoDBResourcesCreateUpdateMongoDBDatabaseOptionalParams,
  MongoDBResourcesGetMongoDBDatabaseOptionalParams,
  MongoDBResourcesMigrateMongoDBCollectionToManualThroughputOptionalParams,
  MongoDBResourcesMigrateMongoDBCollectionToAutoscaleOptionalParams,
  MongoDBResourcesUpdateMongoDBCollectionThroughputOptionalParams,
  MongoDBResourcesGetMongoDBCollectionThroughputOptionalParams,
  MongoDBResourcesMigrateMongoDBDatabaseToManualThroughputOptionalParams,
  MongoDBResourcesMigrateMongoDBDatabaseToAutoscaleOptionalParams,
  MongoDBResourcesUpdateMongoDBDatabaseThroughputOptionalParams,
  MongoDBResourcesGetMongoDBDatabaseThroughputOptionalParams,
} from "./api/mongoDBResources/index.js";
export type {
  MongoMIResourcesListMongoMIRoleAssignmentsOptionalParams,
  MongoMIResourcesDeleteMongoMIRoleAssignmentOptionalParams,
  MongoMIResourcesCreateUpdateMongoMIRoleAssignmentOptionalParams,
  MongoMIResourcesGetMongoMIRoleAssignmentOptionalParams,
  MongoMIResourcesListMongoMIRoleDefinitionsOptionalParams,
  MongoMIResourcesDeleteMongoMIRoleDefinitionOptionalParams,
  MongoMIResourcesCreateUpdateMongoMIRoleDefinitionOptionalParams,
  MongoMIResourcesGetMongoMIRoleDefinitionOptionalParams,
} from "./api/mongoMIResources/index.js";
export type {
  NotebookWorkspacesStartOptionalParams,
  NotebookWorkspacesRegenerateAuthTokenOptionalParams,
  NotebookWorkspacesListConnectionInfoOptionalParams,
  NotebookWorkspacesListByDatabaseAccountOptionalParams,
  NotebookWorkspacesDeleteOptionalParams,
  NotebookWorkspacesCreateOrUpdateOptionalParams,
  NotebookWorkspacesGetOptionalParams,
} from "./api/notebookWorkspaces/index.js";
export type { OperationsListOptionalParams } from "./api/operations/index.js";
export type { PartitionKeyRangeIdListMetricsOptionalParams } from "./api/partitionKeyRangeId/index.js";
export type { PartitionKeyRangeIdRegionListMetricsOptionalParams } from "./api/partitionKeyRangeIdRegion/index.js";
export type { PercentileListMetricsOptionalParams } from "./api/percentile/index.js";
export type { PercentileSourceTargetListMetricsOptionalParams } from "./api/percentileSourceTarget/index.js";
export type { PercentileTargetListMetricsOptionalParams } from "./api/percentileTarget/index.js";
export type {
  PrivateEndpointConnectionsListByDatabaseAccountOptionalParams,
  PrivateEndpointConnectionsDeleteOptionalParams,
  PrivateEndpointConnectionsCreateOrUpdateOptionalParams,
  PrivateEndpointConnectionsGetOptionalParams,
} from "./api/privateEndpointConnections/index.js";
export type {
  PrivateLinkResourcesListByDatabaseAccountOptionalParams,
  PrivateLinkResourcesGetOptionalParams,
} from "./api/privateLinkResources/index.js";
export type {
  RestorableDatabaseAccountsListOptionalParams,
  RestorableDatabaseAccountsListByLocationOptionalParams,
  RestorableDatabaseAccountsGetByLocationOptionalParams,
} from "./api/restorableDatabaseAccounts/index.js";
export type { RestorableGremlinDatabasesListOptionalParams } from "./api/restorableGremlinDatabases/index.js";
export type { RestorableGremlinGraphsListOptionalParams } from "./api/restorableGremlinGraphs/index.js";
export type { RestorableGremlinResourcesListOptionalParams } from "./api/restorableGremlinResources/index.js";
export type { RestorableMongodbCollectionsListOptionalParams } from "./api/restorableMongodbCollections/index.js";
export type { RestorableMongodbDatabasesListOptionalParams } from "./api/restorableMongodbDatabases/index.js";
export type { RestorableMongodbResourcesListOptionalParams } from "./api/restorableMongodbResources/index.js";
export type { RestorableSqlContainersListOptionalParams } from "./api/restorableSqlContainers/index.js";
export type { RestorableSqlDatabasesListOptionalParams } from "./api/restorableSqlDatabases/index.js";
export type { RestorableSqlResourcesListOptionalParams } from "./api/restorableSqlResources/index.js";
export type { RestorableTableResourcesListOptionalParams } from "./api/restorableTableResources/index.js";
export type { RestorableTablesListOptionalParams } from "./api/restorableTables/index.js";
export type {
  ServiceListOptionalParams,
  ServiceDeleteOptionalParams,
  ServiceCreateOptionalParams,
  ServiceGetOptionalParams,
} from "./api/service/index.js";
export type {
  SqlResourcesListSqlRoleAssignmentsOptionalParams,
  SqlResourcesDeleteSqlRoleAssignmentOptionalParams,
  SqlResourcesCreateUpdateSqlRoleAssignmentOptionalParams,
  SqlResourcesGetSqlRoleAssignmentOptionalParams,
  SqlResourcesListSqlRoleDefinitionsOptionalParams,
  SqlResourcesDeleteSqlRoleDefinitionOptionalParams,
  SqlResourcesCreateUpdateSqlRoleDefinitionOptionalParams,
  SqlResourcesGetSqlRoleDefinitionOptionalParams,
  SqlResourcesListSqlTriggersOptionalParams,
  SqlResourcesDeleteSqlTriggerOptionalParams,
  SqlResourcesCreateUpdateSqlTriggerOptionalParams,
  SqlResourcesGetSqlTriggerOptionalParams,
  SqlResourcesListSqlUserDefinedFunctionsOptionalParams,
  SqlResourcesDeleteSqlUserDefinedFunctionOptionalParams,
  SqlResourcesCreateUpdateSqlUserDefinedFunctionOptionalParams,
  SqlResourcesGetSqlUserDefinedFunctionOptionalParams,
  SqlResourcesListSqlStoredProceduresOptionalParams,
  SqlResourcesDeleteSqlStoredProcedureOptionalParams,
  SqlResourcesCreateUpdateSqlStoredProcedureOptionalParams,
  SqlResourcesGetSqlStoredProcedureOptionalParams,
  SqlResourcesRetrieveContinuousBackupInformationOptionalParams,
  SqlResourcesListSqlContainersOptionalParams,
  SqlResourcesDeleteSqlContainerOptionalParams,
  SqlResourcesCreateUpdateSqlContainerOptionalParams,
  SqlResourcesGetSqlContainerOptionalParams,
  SqlResourcesListClientEncryptionKeysOptionalParams,
  SqlResourcesCreateUpdateClientEncryptionKeyOptionalParams,
  SqlResourcesGetClientEncryptionKeyOptionalParams,
  SqlResourcesMigrateSqlContainerToManualThroughputOptionalParams,
  SqlResourcesMigrateSqlContainerToAutoscaleOptionalParams,
  SqlResourcesUpdateSqlContainerThroughputOptionalParams,
  SqlResourcesGetSqlContainerThroughputOptionalParams,
  SqlResourcesMigrateSqlDatabaseToManualThroughputOptionalParams,
  SqlResourcesMigrateSqlDatabaseToAutoscaleOptionalParams,
  SqlResourcesUpdateSqlDatabaseThroughputOptionalParams,
  SqlResourcesGetSqlDatabaseThroughputOptionalParams,
  SqlResourcesListSqlDatabasesOptionalParams,
  SqlResourcesDeleteSqlDatabaseOptionalParams,
  SqlResourcesCreateUpdateSqlDatabaseOptionalParams,
  SqlResourcesGetSqlDatabaseOptionalParams,
} from "./api/sqlResources/index.js";
export type {
  TableResourcesListTableRoleAssignmentsOptionalParams,
  TableResourcesDeleteTableRoleAssignmentOptionalParams,
  TableResourcesCreateUpdateTableRoleAssignmentOptionalParams,
  TableResourcesGetTableRoleAssignmentOptionalParams,
  TableResourcesListTableRoleDefinitionsOptionalParams,
  TableResourcesDeleteTableRoleDefinitionOptionalParams,
  TableResourcesCreateUpdateTableRoleDefinitionOptionalParams,
  TableResourcesGetTableRoleDefinitionOptionalParams,
  TableResourcesRetrieveContinuousBackupInformationOptionalParams,
  TableResourcesListTablesOptionalParams,
  TableResourcesDeleteTableOptionalParams,
  TableResourcesCreateUpdateTableOptionalParams,
  TableResourcesGetTableOptionalParams,
  TableResourcesMigrateTableToManualThroughputOptionalParams,
  TableResourcesMigrateTableToAutoscaleOptionalParams,
  TableResourcesUpdateTableThroughputOptionalParams,
  TableResourcesGetTableThroughputOptionalParams,
} from "./api/tableResources/index.js";
export type {
  CassandraClustersOperations,
  CassandraDataCentersOperations,
  CassandraResourcesOperations,
  CollectionOperations,
  CollectionPartitionOperations,
  CollectionPartitionRegionOperations,
  CollectionRegionOperations,
  DatabaseOperations,
  DatabaseAccountRegionOperations,
  DatabaseAccountsOperations,
  FleetOperations,
  FleetspaceOperations,
  FleetspaceAccountOperations,
  GremlinResourcesOperations,
  LocationsOperations,
  MongoDBResourcesOperations,
  MongoMIResourcesOperations,
  NotebookWorkspacesOperations,
  OperationsOperations,
  PartitionKeyRangeIdOperations,
  PartitionKeyRangeIdRegionOperations,
  PercentileOperations,
  PercentileSourceTargetOperations,
  PercentileTargetOperations,
  PrivateEndpointConnectionsOperations,
  PrivateLinkResourcesOperations,
  RestorableDatabaseAccountsOperations,
  RestorableGremlinDatabasesOperations,
  RestorableGremlinGraphsOperations,
  RestorableGremlinResourcesOperations,
  RestorableMongodbCollectionsOperations,
  RestorableMongodbDatabasesOperations,
  RestorableMongodbResourcesOperations,
  RestorableSqlContainersOperations,
  RestorableSqlDatabasesOperations,
  RestorableSqlResourcesOperations,
  RestorableTableResourcesOperations,
  RestorableTablesOperations,
  ServiceOperations,
  SqlResourcesOperations,
  TableResourcesOperations,
} from "./classic/index.js";
export type { PageSettings, ContinuablePage, PagedAsyncIterableIterator };
export { AzureClouds };
export type { AzureSupportedClouds };
export { RestError, isRestError } from "@azure/core-rest-pipeline";
